import React from 'react';
import { Link } from 'react-router-dom';
import { Sun, ArrowLeft, CheckCircle, Moon, Lightbulb, Eye } from 'lucide-react';
import QuoteForm from '../components/QuoteForm';

const TechEclairagePage = () => {
  const images = {
    colorNight: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fs_cameras_colors_in_the_dark_sm_1f87637e11%401.png",
    nightVision: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fcameras_hlvf_ai_view_0dc05637cd%401.jpg",
  };

  return (
    <div className="min-h-screen py-8 md:py-12" data-testid="tech-eclairage-page">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-8">
        <Link to="/superior-video" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Retour à la vidéosurveillance</span>
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/40 rounded-full mb-4">
              <Sun className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-400 font-semibold">Vision nocturne avancée</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-4">
              Éclairage <span className="text-yellow-400">Hybride</span>
            </h1>
            <p className="text-lg text-white/50 mb-6 leading-relaxed">
              Grâce à l'éclairage hybride, les caméras Superior enregistrent en couleur même la nuit. 
              La combinaison IR + lumière blanche offre une vision claire et une dissuasion active.
            </p>
            <a href="#devis" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold rounded-lg transition-colors">
              Demander un devis
            </a>
          </div>
          <div className="rounded-xl overflow-hidden bg-black aspect-video">
            <video 
              autoPlay 
              loop 
              muted 
              playsInline
              preload="auto"
              className="w-full h-full object-contain"
              poster={images.colorNight}
            >
              <source src="/videos/night-vision.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Section: Voir en couleur même dans l'obscurité */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="bg-gradient-to-br from-yellow-900/20 to-fgs-surface border border-yellow-500/20 rounded-2xl p-6 md:p-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
            Voir en couleur même dans l'obscurité
          </h2>
          <p className="text-white/50 mb-8 max-w-3xl leading-relaxed">
            Lorsqu'un mouvement ou un objet est détecté, la caméra passe automatiquement de l'infrarouge (IR) 
            à la lumière blanche pour capturer tous les détails en couleur. Le flash de lumière blanche soudain 
            agit également comme un facteur de dissuasion, immobilisant les intrus potentiels.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-black/40/50 rounded-xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Moon className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Éclairage infrarouge</h4>
                  <p className="text-sm text-white/50">Vision nocturne discrète</p>
                </div>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-white">
                  <CheckCircle className="w-4 h-4 text-red-400" />
                  Portée jusqu'à 60 mètres (196 ft)
                </li>
                <li className="flex items-center gap-2 text-sm text-white">
                  <CheckCircle className="w-4 h-4 text-red-400" />
                  Invisible à l'œil nu (850nm)
                </li>
                <li className="flex items-center gap-2 text-sm text-white">
                  <CheckCircle className="w-4 h-4 text-red-400" />
                  Surveillance discrète 24h/24
                </li>
              </ul>
            </div>

            <div className="bg-black/40/50 rounded-xl p-6 border border-yellow-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Lumière blanche</h4>
                  <p className="text-sm text-white/50">Dissuasion active</p>
                </div>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-white">
                  <CheckCircle className="w-4 h-4 text-yellow-400" />
                  Portée jusqu'à 40 mètres (131 ft)
                </li>
                <li className="flex items-center gap-2 text-sm text-white">
                  <CheckCircle className="w-4 h-4 text-yellow-400" />
                  Activation automatique sur détection
                </li>
                <li className="flex items-center gap-2 text-sm text-white">
                  <CheckCircle className="w-4 h-4 text-yellow-400" />
                  Enregistrement couleur de nuit
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça fonctionne */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-8 text-center">
          Comment fonctionne l'éclairage hybride ?
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { step: "1", title: "Mode IR par défaut", desc: "La caméra utilise l'infrarouge pour une surveillance discrète et continue, sans alerter les intrus." },
            { step: "2", title: "Détection de mouvement", desc: "L'IA détecte un mouvement ou un objet spécifique (personne, véhicule) dans le champ de vision." },
            { step: "3", title: "Flash lumière blanche", desc: "La caméra active instantanément la lumière blanche pour capturer les couleurs et dissuader l'intrus." },
          ].map((item, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-yellow-400 font-bold text-lg">{item.step}</span>
              </div>
              <h4 className="text-white font-semibold mb-2">{item.title}</h4>
              <p className="text-sm text-white/50">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Avantages */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-10">
          <h2 className="text-2xl font-semibold text-white uppercase mb-8 text-center">
            Avantages de l'éclairage hybride
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Eye, title: "Couleurs de nuit", desc: "Identification précise des vêtements et véhicules" },
              { icon: Lightbulb, title: "Dissuasion active", desc: "Le flash surprend et immobilise les intrus" },
              { icon: Moon, title: "Mode discret", desc: "IR invisible pour surveillance non détectée" },
              { icon: Sun, title: "Transition auto", desc: "Passage automatique IR/Blanc selon la détection" },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-xl bg-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-yellow-400" />
                </div>
                <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                <p className="text-sm text-white/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section id="devis" className="max-w-[1400px] mx-auto px-4 md:px-12 scroll-mt-24">
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-10">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                Protégez votre site 24h/24
              </h2>
              <p className="text-white/50 mb-6">
                Toutes les caméras Superior intègrent l'éclairage hybride pour une protection jour et nuit.
              </p>
              <div className="space-y-3">
                {[
                  "IR jusqu'à 60m + Lumière blanche 40m",
                  "Transition automatique selon détection IA",
                  "Installation par techniciens certifiés",
                  "Garantie 4 ans FGS",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <QuoteForm category="video" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechEclairagePage;
